import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PropertyDetails } from '../../../../core/models/property-details.model';

type PriceInvestmentAnalysis = PropertyDetails['price_investment_analysis'];

@Component({
  selector: 'app-investment-analysis',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="surface-card p-6 md:p-8 text-text-primary shadow-lg relative overflow-hidden border border-border antialiased select-none"
    >
      <div
        class="absolute -right-24 -top-24 w-48 h-48 bg-primary/10 blur-3xl rounded-full pointer-events-none"
      ></div>

      <div class="flex items-center gap-3 mb-6 pb-4 border-b border-border-subtle relative z-10">
        <div
          class="w-8 h-8 rounded-xl bg-surface-elevated border border-border flex items-center justify-center text-primary shadow-sm"
        >
          <i class="fa-solid fa-chart-line-up text-xs"></i>
        </div>
        <div>
          <h4 class="text-[10px] font-black uppercase tracking-widest text-text-muted font-mono">
            محرك العائد المالي
          </h4>
          <h2 class="text-lg font-black text-text-primary tracking-tight mt-0.5">تحليل السعر والاستثمار</h2>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 relative z-10">
        <div
          class="bg-bg-secondary/40 border border-border-subtle rounded-2xl p-4 transition-colors hover:bg-surface-hover hover:border-primary/20"
        >
          <div
            class="text-text-muted text-[10px] font-black uppercase tracking-wider font-mono mb-1.5"
          >
            سعر المتر المربع
          </div>
          <div class="text-xl sm:text-2xl font-black font-mono text-text-primary tracking-tight mb-2">
            {{ valueOrDefault(analysis?.price_per_sqm) }}
          </div>
          <div class="flex items-center gap-2 text-[11px] font-semibold text-success">
            <i class="fa-solid fa-arrow-trend-up text-[10px]"></i>
            <span class="truncate">{{ valueOrDefault(analysis?.comparison_to_area_average) }}</span>
          </div>
        </div>

        <div
          class="bg-bg-secondary/40 border border-border-subtle rounded-2xl p-4 transition-colors hover:bg-surface-hover hover:border-primary/20"
        >
          <div
            class="text-text-muted text-[10px] font-black uppercase tracking-wider font-mono mb-1.5"
          >
            العائد الإيجاري المتوقع
          </div>
          <div class="text-xl sm:text-2xl font-black font-mono text-text-primary tracking-tight mb-2">
            {{ valueOrDefault(analysis?.expected_rental_yield) }}
          </div>
          <div class="text-[11px] font-medium text-text-muted font-mono">
            العائد السنوي:
            <span class="text-text-primary font-bold">{{
              valueOrDefault(analysis?.expected_rental_annual_return)
            }}</span>
          </div>
        </div>

        <div
          class="bg-bg-secondary/40 border border-border-subtle rounded-2xl p-4 transition-colors hover:bg-surface-hover hover:border-primary/20"
        >
          <div
            class="text-text-muted text-[10px] font-black uppercase tracking-wider font-mono mb-1.5"
          >
            الزيادة المتوقعة
          </div>
          <div class="text-xl sm:text-2xl font-black font-mono text-text-primary tracking-tight mb-2">
            {{ valueOrDefault(analysis?.expected_appreciation) }}
          </div>
          <div
            class="text-[11px] font-medium text-text-muted font-mono uppercase tracking-wide text-[9px] font-bold"
          >
            النمو السنوي المتوقع للمنطقة
          </div>
        </div>

        <div
          class="bg-bg-secondary/40 border border-border-subtle rounded-2xl p-4 transition-colors hover:bg-surface-hover hover:border-primary/20"
        >
          <div
            class="text-text-muted text-[10px] font-black uppercase tracking-wider font-mono mb-1.5"
          >
            فترة الاسترداد
          </div>
          <div class="text-xl sm:text-2xl font-black font-mono text-text-primary tracking-tight mb-2">
            {{ formatPayback(analysis?.payback_period_years) }}
          </div>
          <div
            class="text-[11px] font-medium text-text-muted font-mono uppercase tracking-wide text-[9px] font-bold"
          >
            الاستهلاك عبر دخل الإيجار
          </div>
        </div>
      </div>

      <div
        class="mt-5 rounded-2xl bg-bg-secondary/30 border border-dashed border-border-subtle p-5 relative z-10"
      >
        <h3
          class="text-[10px] font-black uppercase tracking-widest text-primary font-mono mb-1.5"
        >
          ملخص تنفيذي
        </h3>
        <p class="text-xs font-medium text-text-secondary leading-relaxed normal-case select-text">
          {{
            valueOrDefault(
              summary,
              'ملخص تحليل استثمار الأصول الشامل في انتظار الحساب.'
            )
          }}
        </p>

        <div
          class="mt-4 pt-3 border-t border-border-subtle flex items-center justify-between text-[10px] font-bold font-mono uppercase tracking-wide text-text-muted"
        >
          <span>تدفق تدقيق البيانات</span>
          <div class="flex items-center gap-1.5">
            <i class="fa-solid fa-clock-rotate-left text-[10px]"></i>
            <span>
              تحديث:
              @if (lastUpdated) {
                <span class="text-text-secondary font-black">{{
                  lastUpdated | date: 'mediumDate'
                }}</span>
              } @else {
                <span class="text-text-muted">غير متوفر</span>
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class InvestmentAnalysisComponent {
  @Input() analysis: PriceInvestmentAnalysis | null = null;
  @Input() summary: string = '';
  @Input() lastUpdated: string = '';

  valueOrDefault(value: unknown, fallback: string = 'غير متوفر'): string {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text ? text : fallback;
  }

  formatPayback(value: unknown): string {
    if (typeof value === 'number') {
      if (value > 0) return `${value} سنوات`;
      if (value === 0) return 'عائد فوري';
    }
    return 'غير متوفر';
  }
}
